/**
 * StarRating - Interaktive Sterne-Bewertung
 *
 * Features:
 * - 0-5 Sterne auswählbar
 * - Hover-Vorschau
 * - Touch-optimiert (44px Mindestgröße)
 * - Farbcodierung je nach Bewertung
 * - Readonly-Modus für Anzeige
 */

import { useState } from 'react';
import { Star } from 'lucide-react';
import type { StarRating as StarRatingType } from '@/types/rating.types';
import { STAR_LABELS } from '@/types/rating.types';
import { getStarClasses, getStarLevel } from '@/config/ui.config';
import { cn } from '@/lib/utils';

interface StarRatingProps {
  /** Aktueller Wert (0-5) */
  value: StarRatingType;
  /** Callback bei Änderung */
  onChange?: (value: StarRatingType) => void;
  /** Nur-Lesen-Modus */
  readonly?: boolean;
  /** Größe der Sterne */
  size?: 'sm' | 'md' | 'lg';
  /** Label anzeigen */
  showLabel?: boolean;
  /** Kompakter Modus (kleinere Abstände) */
  compact?: boolean;
  /** Zusätzliche CSS-Klassen */
  className?: string;
  /** Barrierefreiheits-Label für die Gruppe */
  ariaLabel?: string;
}

const STAR_SIZES = {
  sm: 'w-5 h-5',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
};

const CONTAINER_GAPS = {
  sm: 'gap-0.5',
  md: 'gap-1',
  lg: 'gap-1.5',
};

export function StarRating({
  value,
  onChange,
  readonly = false,
  size = 'md',
  showLabel = false,
  compact = false,
  className,
  ariaLabel = 'Bewertung',
}: StarRatingProps): JSX.Element {
  const [hoverValue, setHoverValue] = useState<StarRatingType | null>(null);

  const displayValue = hoverValue ?? value;
  const isInteractive = !readonly && onChange;

  const handleStarClick = (starIndex: number): void => {
    if (!isInteractive) return;

    const newValue = (starIndex + 1) as StarRatingType;
    // Wenn bereits der gleiche Wert ausgewählt ist, auf 0 zurücksetzen (Abwahl)
    if (newValue === value) {
      onChange(0);
    } else {
      onChange(newValue);
    }
  };

  const handleMouseEnter = (starIndex: number): void => {
    if (!isInteractive) return;
    setHoverValue((starIndex + 1) as StarRatingType);
  };

  const handleMouseLeave = (): void => {
    if (!isInteractive) return;
    setHoverValue(null);
  };

  const getStarColorClass = (starIndex: number): string => {
    const effectiveValue = displayValue;
    if (starIndex < effectiveValue) {
      // Gefüllter Stern - Farbe basiert auf aktuellem/hover Wert
      return getStarClasses(effectiveValue).starColor;
    }
    // Leerer Stern
    return 'text-gray-300';
  };

  const level = getStarLevel(displayValue);
  const label = STAR_LABELS[displayValue];

  return (
    <div className={cn('flex flex-col', className)}>
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={5}
        className={cn(
          'flex items-center',
          compact ? CONTAINER_GAPS.sm : CONTAINER_GAPS[size]
        )}
        onMouseLeave={handleMouseLeave}
      >
        {[0, 1, 2, 3, 4].map((starIndex) => {
          const isFilled = starIndex < displayValue;

          return (
            <button
              key={starIndex}
              type="button"
              role="radio"
              aria-checked={value === starIndex + 1}
              disabled={readonly}
              onClick={() => handleStarClick(starIndex)}
              onMouseEnter={() => handleMouseEnter(starIndex)}
              className={cn(
                'transition-all duration-150',
                isInteractive && [
                  'cursor-pointer',
                  'hover:scale-110',
                  'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 rounded',
                  'min-w-11 min-h-11 flex items-center justify-center', // Touch target
                ],
                readonly && 'cursor-default'
              )}
              aria-label={`${starIndex + 1} Stern${starIndex > 0 ? 'e' : ''}`}
            >
              <Star
                className={cn(
                  STAR_SIZES[size],
                  'transition-colors duration-150',
                  getStarColorClass(starIndex),
                  isFilled && 'fill-current'
                )}
              />
            </button>
          );
        })}
      </div>

      {showLabel && (
        <span
          className={cn(
            'text-sm mt-1',
            level === 'unrated' ? 'text-gray-500' : getStarClasses(displayValue).text
          )}
        >
          {label}
        </span>
      )}
    </div>
  );
}

/**
 * Kompakte Variante für Listen und Tabellen
 */
export function StarRatingCompact({
  value,
  onChange,
  readonly = false,
  className,
}: Omit<StarRatingProps, 'size' | 'showLabel' | 'compact'>): JSX.Element {
  return (
    <StarRating
      value={value}
      onChange={onChange}
      readonly={readonly}
      size="sm"
      compact
      className={className}
    />
  );
}

/**
 * Inline-Anzeige (nur Sterne ohne Interaktion)
 */
export function StarRatingInline({
  value,
  size = 'sm',
  className,
}: Pick<StarRatingProps, 'value' | 'size' | 'className'>): JSX.Element {
  return (
    <StarRating value={value} readonly size={size} compact className={className} />
  );
}
